import apiEndPoints from '../../apiconstants';
import { GetUrlSourceListResponse } from '../advisor/advisortypes';
import { api, deafultResponseAPIResponse, handleErrorResponse } from '../api';
import {
  AdvisorKnowledgeListApiResponse,
  AdvisorSpecificKnowledgeAPIResponse,
  CreateDictionaryPayload,
  CreateExpertAnswerPayload,
  CreateMetadataDefinitionPayload,
  DictionaryBulkUploadPayload,
  DictionaryTemplateUrlResponse,
  DictionaryMutationApiResponse,
  DictionaryPresignedUrlItem,
  DictionaryQueryApiResponse,
  DictionaryQueryData,
  DictionaryQueryPayload,
  UpdateDictionaryKnowledgePayload,
  UpdateDictionaryPayload,
  expertAnswerKnowledgeMetadataPayload,
  GetAdvisorKnowledgeDetailsArg,
  GetKnowledgeMetadataApiResponse,
  GetMetadataDefinitionsApiResponse,
  KnowledgeMetadataKeysResult,
  Metadata,
  MetadataDefinitionMutationApiResponse,
  PresignedUrlResponse,
  UpdateKnowledgeMetadataApiResponse,
  UpdateMetadataDefinitionPayload,
  UploadKnowledgeArticlePayload,
  UrlSourceListUpload,
} from './knowledgetypes';

/**
 * Parent knowledge API may return a `presigned_url` instead of inlining large table payloads.
 * Pagination/filters still go on the parent call; row data is loaded from the URL when present.
 * Presigned payload shape matches `response.data`.
 */
async function resolveAdvisorKnowledgeDetailsData(response: any) {
  const data = response?.data;
  const presignedUrl = data?.presigned_url;
  if (typeof presignedUrl === 'string' && presignedUrl.length > 0) {
    const presignedResponse = await fetch(presignedUrl);
    if (!presignedResponse.ok) {
      throw new Error(
        `Failed to load knowledge details from presigned URL (${presignedResponse.status})`
      );
    }
    return presignedResponse.json();
  }
  return data;
}

export const knowledgeApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    getKnowledgeAdvisorList: builder.query<
      AdvisorKnowledgeListApiResponse,
      void
    >({
      query: () => apiEndPoints?.getKnowledgeAdvisorList,
      providesTags: (result, error, id) => [
        { type: 'Knowledge', id: String(id) },
      ],
      transformErrorResponse: handleErrorResponse,
    }),
    getAdvisorKnowledgeDetails: builder.query<
      AdvisorSpecificKnowledgeAPIResponse,
      GetAdvisorKnowledgeDetailsArg
    >({
      query: ({
        advisorId,
        pagination,
        column_filters,
        metadata_filters,
        sorting,
      }) => ({
        url: `${apiEndPoints?.getAdvisorKnowledgeDetails}/${advisorId}`,
        method: 'POST',
        body: {
          pagination,
          column_filters,
          metadata_filters,
          ...(sorting ? { sorting } : {}),
        },
      }),
      providesTags: (result, error, arg) => [
        { type: 'Knowledge', id: String(arg.advisorId) },
      ],
      transformResponse: resolveAdvisorKnowledgeDetailsData,
      transformErrorResponse: handleErrorResponse,
    }),
    fetchAdvisorKnowledgeDetails: builder.mutation<
      any,
      GetAdvisorKnowledgeDetailsArg
    >({
      query: ({
        advisorId,
        pagination,
        column_filters,
        metadata_filters,
        sorting,
      }) => ({
        url: `${apiEndPoints?.getAdvisorKnowledgeDetails}/${advisorId}`,
        method: 'POST',
        body: {
          pagination,
          column_filters,
          metadata_filters,
          ...(sorting ? { sorting } : {}),
        },
      }),
      transformResponse: resolveAdvisorKnowledgeDetailsData,
      transformErrorResponse: handleErrorResponse,
    }),
    getKnowledgeMetadataKeys: builder.query<
      KnowledgeMetadataKeysResult,
      void
    >({
      query: () => ({
        url: apiEndPoints?.getKnowledgeMetadataKeys,
        method: 'GET',
      }),
      transformResponse: (response: any): KnowledgeMetadataKeysResult => ({
        keys: Array.isArray(response?.data?.keys) ? response.data.keys : [],
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getPresignedUrl: builder.mutation<
      PresignedUrlResponse,
      | { advisor_id: string; data: { filename: string; parts: number }[] }
      | DictionaryPresignedUrlItem[]
    >({
      query: (body) => ({
        url: apiEndPoints?.getAidesPresignedUrl,
        method: 'POST',
        body,
      }),
      transformErrorResponse: handleErrorResponse,
    }),

    getUploadUrlList: builder.mutation<
      GetUrlSourceListResponse,
      { knowledgeUploadURl: string; advisor_id: string }
    >({
      query: ({ knowledgeUploadURl, advisor_id }) => ({
        url: `${apiEndPoints?.getKnowledgeUrlSources}`,
        method: 'POST',
        body: {
          url: knowledgeUploadURl,
          advisor_id: advisor_id,
        },
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    uploadUrlSourceList: builder.mutation<
      deafultResponseAPIResponse,
      UrlSourceListUpload
    >({
      query: (UrlSourceListUpload) => ({
        url: `${apiEndPoints?.uploadUrlSourceList}`,
        method: 'POST',
        body: UrlSourceListUpload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    delinkKnowledge: builder.mutation<
      deafultResponseAPIResponse,
      { advisor_id: string; source_id: string }
    >({
      query: ({ advisor_id, source_id }) => ({
        url: `${apiEndPoints?.delinkKnowledge}?advisor_id=${advisor_id}&source_id=${source_id}`,
        method: 'PATCH',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    deleteKnowledge: builder.mutation<
      deafultResponseAPIResponse,
      { advisor_id: string; source_id: string }
    >({
      query: ({ advisor_id, source_id }) => ({
        url: `${apiEndPoints?.deleteKnowledge}?advisor_id=${advisor_id}&source_id=${source_id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getKnowledgeExpertAnswer: builder.query<any, string>({
      query: (expert_id) => `${apiEndPoints?.getKnowledgeArticle}/${expert_id}`,
      // providesTags: (result, error, id) => [{ type: "Knowledge", id: String(id) }], //TODO check this
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse,
    }),
    deleteExpertAnswer: builder.mutation<
      deafultResponseAPIResponse,
      { expert_id: string }
    >({
      query: ({ expert_id }) => ({
        url: `${apiEndPoints?.deleteKnowledgeArticle}/${expert_id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    delinkExpertAnswer: builder.mutation<
      deafultResponseAPIResponse,
      { advisor_id: string; expert_id: string }
    >({
      query: ({ advisor_id, expert_id }) => ({
        url: `${apiEndPoints?.delinkKnowledgeArticle}?advisor_id=${advisor_id}&article_id=${expert_id}`,
        method: 'PATCH',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    updateExpertAnswerKnowledgeMetaData: builder.mutation<
      deafultResponseAPIResponse,
      { article_id: string; data: expertAnswerKnowledgeMetadataPayload }
    >({
      query: ({ article_id, data }) => ({
        url: `${apiEndPoints?.uploadKnowledgeArticle}/${article_id}`,
        method: 'PATCH',
        body: data,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getAdvisorKnowledgeMetadata: builder.query<
      GetKnowledgeMetadataApiResponse,
      { advisor_id: string }
    >({
      query: ({ advisor_id }) =>
        `${apiEndPoints?.getAdvisorKnowledgeMetadata}/${advisor_id}`,
      transformErrorResponse: handleErrorResponse,
    }),
    getKnowledgeArticleMetadata: builder.query<
      GetKnowledgeMetadataApiResponse,
      { article_id: string }
    >({
      query: ({ article_id }) =>
        `${apiEndPoints?.getKnowledgeArticleMetadata}/${article_id}`,
      transformErrorResponse: handleErrorResponse,
    }),

    updateAdvisorKnowledgeMetadata: builder.mutation<
      UpdateKnowledgeMetadataApiResponse,
      { source_id: string; details: Metadata[] }
    >({
      query: ({ source_id, details }) => ({
        url: apiEndPoints?.updateAdvisorKnowledgeMetadata,
        method: 'PATCH',
        body: {
          source_id: source_id,
          updates: details,
        },
      }),
      transformErrorResponse: handleErrorResponse,
    }),

    updateAdvisorKnowledgeDatabricks: builder.mutation<
      deafultResponseAPIResponse,
      { advisor_id: string; sourceIdList: string[] }
    >({
      query: ({ advisor_id, sourceIdList }) => ({
        url: apiEndPoints?.updateAdvisorKnowledgeDatabricks,
        method: 'PATCH',
        body: {
          advisor_id: advisor_id,
          source_id: sourceIdList,
        },
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    trainCreateAdvisorKnowledgeDatabricks: builder.mutation<
      deafultResponseAPIResponse,
      { advisor_id: string; sourceIdList: string[] }
    >({
      query: ({ advisor_id, sourceIdList }) => ({
        url: apiEndPoints?.trainCreateAdvisorKnowledgeDatabricks,
        method: 'POST',
        body: {
          advisor_id: advisor_id,
          source_id: sourceIdList,
        },
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    createKnowledgeArticle: builder.mutation<
      deafultResponseAPIResponse,
      UploadKnowledgeArticlePayload
    >({
      query: (uploadKnowledgeArticlePayload) => ({
        url: apiEndPoints?.uploadKnowledgeArticle,
        method: 'POST',
        body: uploadKnowledgeArticlePayload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    createExpertAnswer: builder.mutation<
      deafultResponseAPIResponse,
      CreateExpertAnswerPayload
    >({
      query: (uploadKnowledgeArticlePayload) => ({
        url: apiEndPoints?.uploadKnowledgeArticle,
        method: 'POST',
        body: uploadKnowledgeArticlePayload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    uploadKnowledgeArticle: builder.mutation<
      deafultResponseAPIResponse,
      { article_id: string; payload: UploadKnowledgeArticlePayload }
    >({
      query: ({ article_id, payload }) => ({
        url: `${apiEndPoints?.uploadKnowledgeArticle}/${article_id}`,
        method: 'PATCH',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getKnowledgeArticle: builder.query<
      any,
      string
    >({
      query: (article_id) =>
        `${apiEndPoints?.getKnowledgeArticle}/${article_id}`,
      transformErrorResponse: handleErrorResponse,
    }),
    deleteKnowledgeArticle: builder.mutation<
      deafultResponseAPIResponse,
      { article_id: string }
    >({
      query: ({ article_id }) => ({
        url: `${apiEndPoints?.deleteKnowledgeArticle}/${article_id}`,
        method: 'DELETE',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    deLinkKnowledgeArticle: builder.mutation<
      deafultResponseAPIResponse,
      { advisor_id: string; article_id: string }
    >({
      query: ({ advisor_id, article_id }) => ({
        url: `${apiEndPoints?.delinkKnowledgeArticle}?advisor_id=${advisor_id}&article_id=${article_id}`,
        method: 'PATCH',
      }),
      transformErrorResponse: handleErrorResponse,
    }),
    getKnowledgeFileTranscript: builder.query<
      any,
      string
    >({
      query: (source_id) =>
        `${apiEndPoints?.getKnowledgeFileTranscript}/${source_id}`,
      transformErrorResponse: handleErrorResponse,
    }),
    getPartsZipComponents: builder.mutation<
      any,
      string
    >({
      query: (source_id) =>
        `${apiEndPoints?.getPartsZipComponents}/${source_id}`,

      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data,
    }),
    getPartsZipComponentDetails: builder.mutation<
      any,
      { source_id: string, componentGroup: string, componentName: string, filename?: string }
    >({

      query: ({ source_id, componentGroup, componentName, filename }) => ({
        url: `${apiEndPoints?.getPartsZipComponentDetails}/${source_id}`,
        method: 'POST',
        body: {
          component_group: componentGroup,
          component_name: componentName,
          // Omitted entirely (not sent as null/empty) when the caller doesn't provide one.
          ...(filename ? { file_name: filename } : {}),
          // Ask the backend to also include the parent component-group's diagram info
          // so the viewer can offer a "Component Group" tab alongside "Component Name".
          return_parent_component: true,
        },
      }),

      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data,
    }),
    // Fetches the presigned_url + coordinates for one specific diagram out of a
    // component/group's `diagrams` list (used for the prev/next image nav arrows).
    getPartsHotspotView: builder.mutation<
      any,
      { source_id: string, component_name: string, file_name: string }
    >({
      query: ({ source_id, component_name, file_name }) => ({
        url: `${apiEndPoints?.getPartsZipComponentDetails}/${source_id}/hotspot-view`,
        method: 'POST',
        body: {
          component_name,
          file_name,
        },
      }),

      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data,
    }),
    getExpertAnswerByMessageId: builder.mutation<
      any,
      { sender_id: string, message_id: string }
    >({

      query: ({ sender_id, message_id }) => ({
        url: `${apiEndPoints?.uploadKnowledgeArticle}?sender_id=${sender_id}&message_id=${message_id}`,
        method: 'GET',
      }),

      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data,
    }),
    fetchKnowledgeArticle: builder.mutation<
      any,
      string
    >({
      query: (article_id) => ({
        url: `${apiEndPoints?.getKnowledgeArticle}/${article_id}`,
        method: 'GET',
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data,
    }),
    fetchGuestKnowledgeArticle: builder.mutation<
      any,
      { article_id: string; token: any }
    >({
      query: ({ article_id, token }) => ({
        url: `${apiEndPoints?.getGuestKnowledgeArticle}/${article_id}`,
        method: 'GET',
        headers: {
          ...(token?.apiToken && { Authorization: `Bearer ${token.apiToken}` }),
          ...(token?.apiKey && { 'x-api-key': token.apiKey }),
        },
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data,
    }),
    viewOntologyTerms: builder.mutation<any, string>({
      query: (source_id) => ({
        url: `${apiEndPoints?.viewOntologyTerms}/${source_id}`,
        method: 'GET',
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response,
    }),
    ontologyGraphUpdate: builder.mutation<any, any>({
      query: (payload) => ({
        url: apiEndPoints?.ontologyGraphUpdate,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response,
    }),
    ontologyTermsAction: builder.mutation<any, { action: string; source_id: string }>({
      query: (payload) => ({
        url: apiEndPoints?.ontologyTermsAction,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response,
    }),
    getMetadataDefinitions: builder.query<GetMetadataDefinitionsApiResponse, void>({
      query: () => ({
        url: apiEndPoints?.metadataDefinitions,
        method: 'GET',
      }),
      providesTags: [{ type: 'Knowledge', id: 'MetadataDefinitions' }],
      transformErrorResponse: handleErrorResponse,
    }),
    createMetadataDefinition: builder.mutation<
      MetadataDefinitionMutationApiResponse,
      CreateMetadataDefinitionPayload
    >({
      query: (payload) => ({
        url: apiEndPoints?.metadataDefinitions,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'MetadataDefinitions' }],
      transformErrorResponse: handleErrorResponse,
    }),
    updateMetadataDefinition: builder.mutation<
      MetadataDefinitionMutationApiResponse,
      { id: string; payload: UpdateMetadataDefinitionPayload }
    >({
      query: ({ id, payload }) => ({
        url: `${apiEndPoints?.metadataDefinitions}/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'MetadataDefinitions' }],
      transformErrorResponse: handleErrorResponse,
    }),
    deleteMetadataDefinition: builder.mutation<deafultResponseAPIResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${apiEndPoints?.metadataDefinitions}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'MetadataDefinitions' }],
      transformErrorResponse: handleErrorResponse,
    }),
    queryDictionary: builder.query<DictionaryQueryData, DictionaryQueryPayload | void>({
      query: (body) => ({
        url: apiEndPoints?.dictionaryQuery,
        method: 'POST',
        body: body ?? {},
      }),
      providesTags: [{ type: 'Knowledge', id: 'DomainDictionary' }],
      transformResponse: (response: DictionaryQueryApiResponse) => response?.data,
      transformErrorResponse: handleErrorResponse,
    }),
    updateDictionary: builder.mutation<
      DictionaryMutationApiResponse,
      { id: string; payload: UpdateDictionaryPayload }
    >({
      query: ({ id, payload }) => ({
        url: `${apiEndPoints?.dictionary}/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'DomainDictionary' }],
      transformErrorResponse: handleErrorResponse,
    }),
    createDictionary: builder.mutation<DictionaryMutationApiResponse, CreateDictionaryPayload>({
      query: (payload) => ({
        url: apiEndPoints?.dictionaryCreate,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'DomainDictionary' }],
      transformErrorResponse: handleErrorResponse,
    }),
    updateDictionaryKnowledge: builder.mutation<
      deafultResponseAPIResponse,
      UpdateDictionaryKnowledgePayload
    >({
      query: (payload) => ({
        url: apiEndPoints?.dictionaryUpdateKnowledge,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'DomainDictionary' }],
      transformErrorResponse: handleErrorResponse,
    }),
    deleteDictionary: builder.mutation<deafultResponseAPIResponse, { ids: string[] }>({
      query: (payload) => ({
        url: apiEndPoints?.dictionary,
        method: 'DELETE',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'DomainDictionary' }],
      transformErrorResponse: handleErrorResponse,
    }),
    bulkUploadDictionary: builder.mutation<deafultResponseAPIResponse, DictionaryBulkUploadPayload>({
      query: (payload) => ({
        url: apiEndPoints?.dictionaryBulkUpload,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Knowledge', id: 'DomainDictionary' }],
      transformErrorResponse: handleErrorResponse,
    }),
    getDictionaryTemplateUrl: builder.mutation<DictionaryTemplateUrlResponse, { s3_key: string }>({
      query: (payload) => ({
        url: apiEndPoints?.getEvaluationsPresignedUrl,
        method: 'POST',
        body: payload,
      }),
      transformErrorResponse: handleErrorResponse,
    }),
  }),
});

export const {
  useGetKnowledgeAdvisorListQuery,
  useGetKnowledgeMetadataKeysQuery,
  useLazyGetKnowledgeMetadataKeysQuery,
  useFetchAdvisorKnowledgeDetailsMutation,
  useGetAdvisorKnowledgeDetailsQuery,
  useGetPresignedUrlMutation,
  useGetUploadUrlListMutation,
  useUploadUrlSourceListMutation,
  useDelinkKnowledgeMutation,
  useDeleteKnowledgeMutation,
  useGetKnowledgeExpertAnswerQuery,
  useDeleteExpertAnswerMutation,
  useDelinkExpertAnswerMutation,
  useGetAdvisorKnowledgeMetadataQuery,
  useGetKnowledgeArticleMetadataQuery,
  useUpdateExpertAnswerKnowledgeMetaDataMutation,
  useUpdateAdvisorKnowledgeMetadataMutation,
  useUpdateAdvisorKnowledgeDatabricksMutation,
  useTrainCreateAdvisorKnowledgeDatabricksMutation,
  useCreateKnowledgeArticleMutation,
  useUploadKnowledgeArticleMutation,
  useGetKnowledgeArticleQuery,
  useDeleteKnowledgeArticleMutation,
  useDeLinkKnowledgeArticleMutation,
  useGetKnowledgeFileTranscriptQuery,
  useGetPartsZipComponentsMutation,
  useGetPartsZipComponentDetailsMutation,
  useGetPartsHotspotViewMutation,
  useGetExpertAnswerByMessageIdMutation,
  useCreateExpertAnswerMutation,
  useFetchKnowledgeArticleMutation,
  useFetchGuestKnowledgeArticleMutation,
  useViewOntologyTermsMutation,
  useOntologyGraphUpdateMutation,
  useOntologyTermsActionMutation,
  useGetMetadataDefinitionsQuery,
  useCreateMetadataDefinitionMutation,
  useUpdateMetadataDefinitionMutation,
  useDeleteMetadataDefinitionMutation,
  useQueryDictionaryQuery,
  useLazyQueryDictionaryQuery,
  useUpdateDictionaryMutation,
  useCreateDictionaryMutation,
  useUpdateDictionaryKnowledgeMutation,
  useDeleteDictionaryMutation,
  useBulkUploadDictionaryMutation,
  useGetDictionaryTemplateUrlMutation,
} = knowledgeApiSlice;
